/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

/* eslint-disable no-unused-vars */
import React, {Component} from 'react';
import ReactDOM from 'react-dom';
import {FormattedMessage, injectIntl} from 'react-intl';
import Button from '@react/react-spectrum/Button';
import TextArea from '@react/react-spectrum/Textarea';
import AnalyticsUtil from '../../../utils/AnalyticsUtil';
import FeedBackAnalytics from './Analytics/FeedBackAnalytics';

/* eslint-enable no-unused-vars */

import './FeedBackSurvey.scss';

interface Props {
	intl: any;
}

interface State {
	feedbackStep: string;
	disableSubmitButton: boolean;
	feedbackValue1?: string;
	feedbackValue2?: string;
	feedbackTextBox1?: any;
	feedbackTextBox2?: any;
}

interface FeedBackSurvey {
	postRenderTextBox1: boolean;
	postRenderTextBox2: boolean;
	feedbackTextBox1: any;
	feedbackTextBox2: any;
}
class FeedBackSurvey extends Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = {
			feedbackStep: 'step1',
			disableSubmitButton: true,
			feedbackTextBox1: '',
			feedbackTextBox2: '',
		};

		this.postRenderTextBox1 = true;
		this.postRenderTextBox2 = true;
	}

	/*
	 * Checks if text is entered in the feedback text box
	 */
	checkTextBoxContent(text: string) {
		// Enable Submit button
		if (text && text.length > 0) {
			this.setState({disableSubmitButton: false});
		} // Disable Submit button
		else {
			this.setState({disableSubmitButton: true});
		}
	}

	/*
	 * Returns the first feedback text box
	 */
	showTextBox1() {
		// The render event gets called multiple times
		// as text is entered. We should only post the event once
		if (this.postRenderTextBox1) {
			const analytics = new FeedBackAnalytics();
			analytics.postFeedBackRender('1');
			this.postRenderTextBox1 = false;
		}

		return (
			<TextArea
				placeholder={this.props.intl.formatMessage({id: 'app.feedbackSurvey.placeholder1'})}
				className='feedback-survey-text step1'
				ref={(component) => {
					this.feedbackTextBox1 = ReactDOM.findDOMNode(component);
				}}
				maxLength='255'
				onChange={e => this.checkTextBoxContent(e)}
			/>
		);
	}

	/*
	 * Returns the second feedback text box
	 */
	showTextBox2() {
		// The render event gets called multiple times
		// as text is entered. We should only post the event once
		if (this.postRenderTextBox2) {
			const analytics = new FeedBackAnalytics();
			analytics.postFeedBackRender('2');
			this.postRenderTextBox2 = false;

			// Select the textbox
			setTimeout(() => {
				if (this.feedbackTextBox2) {
					this.feedbackTextBox2.focus();
					this.feedbackTextBox2.select();
				}
			}, 700);
		}

		return (
			<TextArea
				className='feedback-survey-text step2'
				ref={(component) => {
					this.feedbackTextBox2 = ReactDOM.findDOMNode(component);
				}}
				maxLength='255'
				onChange={e => this.checkTextBoxContent(e)}
			/>
		);
	}

	/*
	 * Submits the entered feedback
	 */
	submitNext(answer: string) {
		this.setState(
			{
				disableSubmitButton: true,
				feedbackStep: 'step2',
				feedbackTextBox1: answer,
			},
			() => {
				const analytics = new FeedBackAnalytics();
				analytics.postFeedBackButtonClick(answer, '1');
			},
		);
	}

	submitFeedback = () => {
		this.setState(
			{
				feedbackTextBox2: this.feedbackTextBox2.value,
				feedbackStep: 'step3',
			},
			() => {
				const analytics = new FeedBackAnalytics();
				analytics.postFeedBackButtonClick(this.state.feedbackTextBox2, '2');
			},
		);

		/* istanbul ignore else */
		if (window.cchome.util.storageAvailable('localStorage')) {
			const userID = window.cchome.util.getUserId();
			localStorage.setItem(`submittedFeedback0819-${userID}`, 'true');
		}
	};

	/*
	 * Shows the next button if text is entered in the first feedback text box
	 */
	showNextButton() {
		// The render event gets called multiple times
		// as text is entered. We should only post the event once
		if (this.postRenderTextBox1) {
			const analytics = new FeedBackAnalytics();
			analytics.postFeedBackRender('1');
			this.postRenderTextBox1 = false;
		}

		return (
			<div className='feedback-survey-button'>
				<Button
					label={this.props.intl.formatMessage({id: 'app.feedbackSurvey.yes.button'})}
					onClick={() => this.submitNext('yes')}
				/>
				<Button
					label={this.props.intl.formatMessage({id: 'app.feedbackSurvey.no.button'})}
					onClick={() => this.submitNext('no')}
				/>
			</div>
		);
	}

	/*
	 * Shows the submit button if text is entered in the second feedback text box
	 */
	showSubmitButton() {
		return (
			<div className='feedback-survey-button'>
				<Button
					label={this.props.intl.formatMessage({id: 'app.feedbackSurvey.send.button'})}
					disabled={this.state.disableSubmitButton}
					onClick={this.submitFeedback}
				/>
			</div>
		);
	}

	renderThankYou() {
		return (
			<div className='feedback-survey-title thanks'>
				<div className='feedback-survey-thanks-header'>
					<div className='feedback-survey-thanks'>
						<FormattedMessage id='app.feedbackSurvey.thanks' />
					</div>
				</div>
			</div>
		);
	}

	/*
	 * Returns the feedback component
	 */
	renderFeedback(): React.ReactNode | null {
		let jsxArr: React.ReactNode | null = null;
		if (this.state.feedbackStep === 'step1') {
			jsxArr = (
				<div className='feedback-survey-body step-1'>
					<div className='feedback-survey-subtitle'>
						<FormattedMessage id={'app.feedbackSurvey.subtitle1'} />
					</div>
					<div className='feedback-survey-compact'>
						<div className='feedback-survey-title'>
							<FormattedMessage id={'app.feedbackSurvey.title1'} />
							{this.showNextButton()}
						</div>
					</div>
				</div>
			);
		}
		else if (this.state.feedbackStep === 'step2') {
			jsxArr = (
				<div className='feedback-survey-body'>
					<div className='feedback-survey-subtitle'>
						<FormattedMessage id={'app.feedbackSurvey.subtitle2'} />
					</div>
					<div className='feedback-survey-title'>
						<FormattedMessage id={'app.feedbackSurvey.title2'} />
					</div>
					<div className='feedback-survey-main'>
						{this.showTextBox2()}
						{this.showSubmitButton()}
					</div>
				</div>
			);
		}
		else if (this.state.feedbackStep === 'step3') {
			jsxArr = this.renderThankYou();
		}
		return jsxArr;
	}

	render() {
		return <div className='feedback-survey home-component'>{this.renderFeedback()}</div>;
	}
}

export default injectIntl(FeedBackSurvey);
